import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { ConfigService } from 'softcat:services/config.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const ConfigPlatforms = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({ config: '' });

    const EditPlatformsSchema = z.object({
        config: z.string().refine(
            (value) => {
                try {
                    JSON.parse(value);

                    return true;
                } catch {
                    return false;
                }
            },
            { message: intl.formatMessage({ id: 'FORM_JSON_VALIDATION' }) },
        ),
    });

    const {
        data: platformData,
        error: platformDataError,
        isLoading: platformDataIsLoading,
    } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllPlatforms` }, ConfigService.findAllPlatforms);

    useEffect(() => {
        if (platformData) {
            setFormValues({
                config: JSON.stringify(platformData, undefined, 4),
            });
        }
    }, [platformData]);

    const onSubmit = async (values: z.infer<typeof EditPlatformsSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (platformData) {
            try {
                await ConfigService.updateAllPlatforms(JSON.parse(values.config));

                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditPlatformsSchema>> defaultValues={formValues} mode={'onChange'} onSubmit={onSubmit} resolver={zodResolver(EditPlatformsSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header description={<FormattedMessage id={'PLATFORMS_PAGE_DESCRIPTION'} />} title={<FormattedMessage id={'PLATFORMS_PAGE_HEADING'} />} />

                {platformDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'PLATFORMS_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {platformDataIsLoading && <Spinner />}

                {platformData && (
                    <>
                        <Form.Textarea label={<FormattedMessage id={'PLATFORMS_FORM_CONFIG_LABEL'} />} name={'config'} rows={16} />

                        <Form.Footer
                            isSubmitting={submitting}
                            submitError={onEditError && <FormattedMessage id={'PLATFORMS_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
